import { useMemo } from "react";
import { useGetList } from "ra-core";

import type { EchoflicksSessionMedia } from "../types";
import { useDisplayableImage } from "../echoflicks/useDisplayableImage";
import {
  findUploadedImage,
  NOAH_KYRA_ABSTAINED_DECISION_COUNT,
  NOAH_KYRA_ACCEPTED_DECISIONS,
  NOAH_KYRA_ECHO_ID,
  NOAH_KYRA_REFERENCE_IMAGES,
  NOAH_KYRA_SAME_FRAME_IMAGES,
} from "./noahKyraIdentityProofData";

type ProofImageProps = {
  media: EchoflicksSessionMedia | null;
  alt: string;
  className: string;
};

const ProofImage = ({ media, alt, className }: ProofImageProps) => {
  const image = useDisplayableImage(media);

  if (!media || (!image.url && !image.isLoading)) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-muted text-xs text-muted-foreground`}
        role="img"
        aria-label={`${alt} unavailable`}
      >
        unavailable
      </div>
    );
  }

  if (!image.url) {
    return (
      <div
        className={`${className} animate-pulse bg-muted`}
        role="img"
        aria-label={`${alt} loading`}
      />
    );
  }

  return <img src={image.url} alt={alt} className={className} loading="lazy" />;
};

export function NoahKyraIdentityProofPage() {
  const {
    data: media = [],
    isPending,
    error,
  } = useGetList<EchoflicksSessionMedia>("echoflicks_session_media", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at_source", order: "ASC" },
    filter: { session_id: NOAH_KYRA_ECHO_ID },
  });

  const mediaByFilename = useMemo(
    () =>
      new Map(
        [
          ...NOAH_KYRA_REFERENCE_IMAGES.Noah,
          ...NOAH_KYRA_REFERENCE_IMAGES.Kyra,
          ...NOAH_KYRA_SAME_FRAME_IMAGES.map((item) => item.filename),
        ].map((filename) => [filename, findUploadedImage(media, filename)]),
      ),
    [media],
  );

  return (
    <main className="mx-auto w-full max-w-[1440px] px-4 py-8 sm:px-8 lg:px-12">
      <header className="border-b border-foreground pb-6">
        <p className="font-mono text-xs text-muted-foreground">
          Echo {NOAH_KYRA_ECHO_ID}
        </p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-6xl">
          Noah / Kyra
        </h1>
        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 font-mono text-sm">
          <span data-testid="accepted-summary">9 / 9 accepted correct</span>
          <span>{NOAH_KYRA_ABSTAINED_DECISION_COUNT} uncertain → Unknown</span>
          <span>2 / 4 same-frame photos fully resolved</span>
        </div>
        {isPending ? (
          <p className="mt-3 text-sm text-muted-foreground">Loading media…</p>
        ) : null}
        {error ? (
          <p className="mt-3 text-sm text-destructive">
            Customer media could not be loaded. The measured results remain
            visible below.
          </p>
        ) : null}
      </header>

      <section
        className="border-b border-border py-8"
        aria-labelledby="references-heading"
      >
        <h2
          id="references-heading"
          className="text-sm font-medium uppercase tracking-[0.18em]"
        >
          Reference identities
        </h2>
        <div className="mt-5 grid border-y border-border md:grid-cols-2 md:divide-x md:divide-border">
          {Object.entries(NOAH_KYRA_REFERENCE_IMAGES).map(
            ([identity, filenames]) => (
              <div key={identity} className="py-5 first:md:pr-6 last:md:pl-6">
                <h3 className="text-2xl font-semibold">{identity}</h3>
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {filenames.map((filename) => (
                    <ProofImage
                      key={filename}
                      media={mediaByFilename.get(filename) ?? null}
                      alt={`${identity} reference ${filename}`}
                      className="aspect-square w-full object-cover"
                    />
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      </section>

      <section
        className="border-b border-border py-8"
        aria-labelledby="same-frame-heading"
      >
        <h2
          id="same-frame-heading"
          className="text-sm font-medium uppercase tracking-[0.18em]"
        >
          Same frame
        </h2>
        <div className="mt-5 grid gap-8 md:grid-cols-2">
          {NOAH_KYRA_SAME_FRAME_IMAGES.map((item) => (
            <figure key={item.filename}>
              <ProofImage
                media={mediaByFilename.get(item.filename) ?? null}
                alt={`${item.filename}: ${item.left} left, ${item.right} right`}
                className="aspect-[4/3] w-full bg-muted object-contain"
              />
              <figcaption className="mt-3 flex items-baseline justify-between gap-4 border-t border-border pt-3 text-sm">
                <span>
                  <span className="block font-mono">{item.filename}</span>
                  <span className="text-muted-foreground">
                    expected {item.left} / {item.right}
                  </span>
                </span>
                <strong>model {item.result}</strong>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section
        className="border-b border-border py-8"
        aria-labelledby="results-heading"
      >
        <h2
          id="results-heading"
          className="text-sm font-medium uppercase tracking-[0.18em]"
        >
          Accepted held-out decisions
        </h2>
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[680px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-foreground">
                <th className="py-3 pr-4 font-medium">Instance</th>
                <th className="px-4 py-3 font-medium">Expected</th>
                <th className="px-4 py-3 font-medium">Result</th>
                <th className="px-4 py-3 text-right font-medium">Similarity</th>
                <th className="py-3 pl-4 text-right font-medium">Margin</th>
              </tr>
            </thead>
            <tbody>
              {NOAH_KYRA_ACCEPTED_DECISIONS.map((decision) => (
                <tr key={decision.instance} className="border-b border-border">
                  <td className="py-3 pr-4 font-mono">{decision.instance}</td>
                  <td className="px-4 py-3">{decision.expected}</td>
                  <td className="px-4 py-3 font-semibold">{decision.result}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {decision.similarity.toFixed(4)}
                  </td>
                  <td className="py-3 pl-4 text-right font-mono">
                    {decision.margin.toFixed(4)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="py-8" aria-labelledby="method-heading">
        <h2
          id="method-heading"
          className="text-sm font-medium uppercase tracking-[0.18em]"
        >
          Method
        </h2>
        <dl className="mt-5 grid border-y border-border text-sm sm:grid-cols-[180px_1fr]">
          <dt className="border-b border-border py-3 font-medium sm:pr-6">
            Detector
          </dt>
          <dd className="border-b border-border py-3 font-mono">
            EfficientDet-Lite0
          </dd>
          <dt className="border-b border-border py-3 font-medium sm:pr-6">
            Identity model
          </dt>
          <dd className="border-b border-border py-3 font-mono">
            MiewID · 2,152 dimensions
          </dd>
          <dt className="border-b border-border py-3 font-medium sm:pr-6">
            Decision
          </dt>
          <dd className="border-b border-border py-3 font-mono">
            nearest reference · similarity ≥ 0.30 · margin ≥ 0.06 · otherwise
            Unknown
          </dd>
          <dt className="py-3 font-medium sm:pr-6">Gemini</dt>
          <dd className="py-3 font-mono">Not used</dd>
        </dl>
        <p className="mt-4 text-xs text-muted-foreground">
          Read-only POC measured on 14 July 2026. Thresholds and MiewID
          licensing are not production-approved.
        </p>
      </section>
    </main>
  );
}

NoahKyraIdentityProofPage.path = "/identity-proof/noah-kyra";
